import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { announcementService } from '../services/announcementService';

const POLL_INTERVAL_MS = 60000;
const STORAGE_KEY = 'notifiedAnnouncementIds';
const ENABLED_KEY = 'announcementNotificationsEnabled';

const getNotifiedIds = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

const saveNotifiedIds = (ids) => {
  const trimmed = [...ids].slice(-500);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
};

const isEnabled = () => {
  const stored = localStorage.getItem(ENABLED_KEY);
  return stored === null ? true : JSON.parse(stored);
};

const clipText = (text, length = 120) => {
  if (!text) return '';
  return text.length > length ? `${text.slice(0, length)}…` : text;
};

const AnnouncementNotifier = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastCheckRef = useRef(new Date());
  const notifiedIdsRef = useRef(getNotifiedIds());

  useEffect(() => {
    if (!user) return;

    const showNotification = (announcement) => {
      if (!isEnabled() || !('Notification' in window) || Notification.permission !== 'granted') {
        return;
      }

      if (notifiedIdsRef.current.has(announcement._id)) return;

      const browserNotification = new Notification(announcement.title || 'New Announcement', {
        body: clipText(announcement.body),
        icon: '/favicon.ico',
        tag: announcement._id,
        requireInteraction: false
      });

      browserNotification.onclick = () => {
        window.focus();
        navigate('/announcements', { state: { openAnnouncementId: announcement._id } });
        browserNotification.close();
      };

      notifiedIdsRef.current.add(announcement._id);
      saveNotifiedIds(notifiedIdsRef.current);
    };

    const checkAnnouncements = async () => {
      const since = lastCheckRef.current;
      const announcements = await announcementService.getNewAnnouncements(since);

      const sorted = announcements
        .sort((a, b) => new Date(a.publishedAt) - new Date(b.publishedAt));

      sorted.forEach(showNotification);

      if (sorted.length > 0) {
        window.dispatchEvent(new CustomEvent('announcements-new'));
      }

      lastCheckRef.current = new Date();
    };

    if (isEnabled() && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    checkAnnouncements();
    const interval = setInterval(checkAnnouncements, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [user, navigate]);

  return null;
};

export default AnnouncementNotifier;
